import React, { useState, useEffect } from 'react';
import { SbModal, SbForm, SbButton } from '../../../../uiCore';
import CoreMarkdownEditor from '../../../../Components/SbMarkdownEditor/CoreMarkdownEditor';
import { VIEWMODE } from '../../../../constants/markdown.constants';
import { ModalDescription, FormatGuideBox, CharCountDisplay } from '../../AdminSetup.styles';

const AboutCompanyModal = ({ visible, onClose, initialData, onSave, cardKey }) => {
  const [form] = SbForm.useForm();
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (visible && initialData) {
      const description = initialData.company_description || '';
      form.setFieldsValue({ description });
      setCharCount(description.length);
      setEditorKey(prev => prev + 1); // Force editor to remount with new data
    }
  }, [visible, initialData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const changedFields = {};
      if (values.description !== (initialData?.company_description || '')) {
        changedFields.company_description = values.description;
      }

      if (Object.keys(changedFields).length === 0) {
        onClose();
        return;
      }

      const result = await onSave(cardKey, changedFields);
      if (result.success) onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SbModal
      title="About Company"
      open={visible}
      onCancel={() => { form.resetFields(); setCharCount(0); onClose(); }}
      width={700}
      footer={[
        <SbButton key="cancel" buttonType="secondary" onClick={() => { form.resetFields(); setCharCount(0); onClose(); }}>Cancel</SbButton>,
        <SbButton key="save" buttonType="primary" loading={loading} onClick={handleSave}>Save</SbButton>
      ]}
    >
      <ModalDescription>
        This is the public-facing company description used in generated JDs and candidate-facing content. Keep it concise and compelling.
      </ModalDescription>

      <FormatGuideBox>
        <span className="guide-title">Format Guide:</span>
        <span className="guide-item">• Use the toolbar to format text with bold, italics, bullets, headings, etc.</span>
        <span className="guide-item">• Organize with sections like <code>Overview</code>, <code>Mission</code>, <code>Vision</code></span>
        <span className="guide-item">• Use bullet points for lists of values, achievements, or products</span>
      </FormatGuideBox>

      <SbForm form={form} layout="vertical">
        <SbForm.Item
          name="description"
          rules={[
            { required: true, message: 'Please enter company description' },
            { min: 50, message: 'Description should be at least 50 characters' },
            { max: 4000, message: 'Maximum 4000 characters' }
          ]}
        >
          <CoreMarkdownEditor
            key={editorKey}
            initialMarkdown={form.getFieldValue('description') || ''}
            onChange={(value) => {
              form.setFieldsValue({ description: value });
              setCharCount(value?.length || 0);
            }}
            viewMode={VIEWMODE.EDIT}
          />
        </SbForm.Item>

        <CharCountDisplay overLimit={charCount > 4000}>
          {charCount} / 4000 characters
          {charCount < 50 && charCount > 0 && (
            <span className="warning">(Minimum 50 characters required)</span>
          )}
        </CharCountDisplay>
      </SbForm>
    </SbModal>
  );
};

export default AboutCompanyModal;
